// src/components/common/SearchComponent.tsx

import { useEffect, useRef, useState } from "react";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import { useTokenSearch } from "@/hooks/useTokenSearch";
import { useSectorSearch } from "@/hooks/useSectorSearch";
import type { SearchSector } from "@/types/search";

interface SearchComponentProps {
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  onSelectToken?: (ticker: string) => void;
  onSelectSector?: (sector: SearchSector) => void;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  placeholder = "Search",
  autoFocus = false,
  className = "",
  onSelectToken,
  onSelectSector,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { results: tokenResults, isLoading } = useTokenSearch(searchValue);
  const { results: sectorResults } = useSectorSearch(searchValue);

  const trimmedQuery = searchValue.trim();
  const dropdownOpen = isFocused && trimmedQuery.length >= 2;

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [autoFocus]);

  const handleSelectToken = (ticker: string) => {
    onSelectToken?.(ticker);
    setSearchValue("");
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleSelectSector = (sector: SearchSector) => {
    onSelectSector?.(sector);
    setSearchValue("");
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Prefer sector match if available, otherwise token
      if (sectorResults[0]) {
        handleSelectSector(sectorResults[0]);
      } else if (tokenResults[0]) {
        handleSelectToken(tokenResults[0].ticker);
      }
    } else if (e.key === "Escape") {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`w-full relative ${className}`}>
      <div className={`${isFocused ? "ring-1 ring-indigo-500/40" : "ring-1 ring-transparent"} px-3 py-2 rounded-md text-sm font-normal bg-gray-800 text-gray-300 hover:bg-gray-700 transition-all duration-200 flex items-center gap-3 w-full`}>
        <DynamicIcon name="search" className="w-4 h-4" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 100)}
          className="bg-transparent outline-none text-sm placeholder-gray-400 w-full"
        />
      </div>

      {dropdownOpen && (
        <div className="absolute mt-1 w-full rounded-md bg-gray-900/95 backdrop-blur border border-gray-700 shadow-2xl overflow-hidden z-50 max-h-96 overflow-auto">
          {/* Sectors Section */}
          {sectorResults.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] tracking-wide uppercase text-gray-400 sticky top-0 bg-gray-900/95">Sectors</div>
              {sectorResults.map((sector) => (
                <button
                  key={sector.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectSector(sector);
                  }}
                  className="flex items-center justify-start w-full px-3 py-2 text-left text-sm hover:bg-gray-800/80"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-5 h-5 rounded flex items-center justify-center bg-${sector.color}-500/20`}>
                      <DynamicIcon
                        name={(sector.iconName || "grid-2x2") as IconName}
                        className={`w-3 h-3 text-${sector.color}-400`}
                      />
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-gray-100 truncate max-w-[180px]">{sector.title}</span>
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Tokens Section */}
          <div className="px-3 py-1.5 text-[10px] tracking-wide uppercase text-gray-400 sticky top-0 bg-gray-900/95">Tokens</div>
          {isLoading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-300">
              <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />
              Searching...
            </div>
          )}
          {!isLoading && tokenResults.length === 0 && sectorResults.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-400">No results for "{trimmedQuery}".</div>
          )}
          {!isLoading && tokenResults.length === 0 && sectorResults.length > 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">No token matches.</div>
          )}
          {!isLoading && tokenResults.length > 0 && (
            <>
              {tokenResults.map((t, idx) => (
                <button
                  key={`${t.ticker}-${idx}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectToken(t.ticker);
                  }}
                  className="flex items-center justify-start w-full px-3 py-2 text-left text-sm hover:bg-gray-800/80"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {t.logo_url ? (
                      <img src={t.logo_url} alt={t.ticker} className="w-5 h-5 rounded" />
                    ) : (
                      <div className="w-5 h-5 rounded bg-gray-600 flex items-center justify-center text-[10px] text-white">{t.ticker.slice(0,1)}</div>
                    )}
                    <div className="flex items-center gap-2 min-w-0">
                      {t.name ? (
                        <>
                          <span className="font-medium text-gray-100 truncate max-w-[180px]">{t.name}</span>
                          <span className="text-gray-400">{t.ticker}</span>
                        </>
                      ) : (
                        <span className="font-medium text-gray-100">{t.ticker}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
